import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import gaTrackingService from '../../../services/gaTrackingService';
import { analyticsService } from '../../../services/analyticsService';
import { useAuth } from '../../../contexts/AuthContext';

const StoreCard = ({ store }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isSaved, setIsSaved] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [mainImageLoaded, setMainImageLoaded] = useState(false);
  const [mainImageError, setMainImageError] = useState(false);
  const [logoLoaded, setLogoLoaded] = useState(false);
  const [logoError, setLogoError] = useState(false);

  const handleSaveToggle = async (e) => {
    e?.stopPropagation();
    const newSavedState = !isSaved;
    setIsSaved(newSavedState);

    // Track in Supabase
    try {
      if (newSavedState && user?.id) {
        await analyticsService?.trackStoreSave(store?.id, user?.id);
      } else if (!newSavedState && user?.id) {
        await analyticsService?.removeStoreSave(store?.id, user?.id);
      }
    } catch (error) {
      console.error('Error tracking save:', error);
    }

    // Track in GA4
    gaTrackingService?.trackStorefrontSave({
      storeId: store?.id,
      storeName: store?.name,
      category: store?.category,
      zone: store?.zone,
      action: newSavedState ? 'save' : 'unsave'
    });
  };

  const handleCardClick = async () => {
    // Track in Supabase
    try {
      const sessionId = `session_${Date.now()}_${Math.random()}`;
      await analyticsService?.trackStoreView(store?.id, user?.id || null, sessionId);
    } catch (error) {
      console.error('Error tracking view:', error);
    }

    // Track in GA4
    gaTrackingService?.trackStorefrontClick({
      storeId: store?.id,
      storeName: store?.name,
      category: store?.category,
      zone: store?.zone,
      source: 'card'
    });

    navigate(`/store-details?id=${store?.id}`);
  };

  const handleImageNavigation = (direction, e) => {
    e?.stopPropagation();
    
    // Reset loading state for new image
    setMainImageLoaded(false);
    setMainImageError(false);
    
    // Track image navigation in GA4
    gaTrackingService?.trackImageNavigation({
      storeId: store?.id,
      direction,
      imageIndex: direction === 'next' 
        ? (currentImageIndex + 1) % store?.images?.length
        : (currentImageIndex - 1 + store?.images?.length) % store?.images?.length
    });

    if (direction === 'next') {
      setCurrentImageIndex((prev) => (prev + 1) % store?.images?.length);
    } else {
      setCurrentImageIndex((prev) => (prev - 1 + store?.images?.length) % store?.images?.length);
    }
  };

  return (
    <div
      onClick={handleCardClick}
      className="bg-card rounded-xl shadow-warm hover:shadow-warm-md transition-smooth cursor-pointer overflow-hidden group"
    >
      <div className="relative h-48 md:h-56 lg:h-64 overflow-hidden bg-muted">
        {/* Skeleton Loader for Main Image */}
        {!mainImageLoaded && !mainImageError && (
          <div className="absolute inset-0 bg-gradient-to-r from-muted via-muted-foreground/10 to-muted animate-pulse" />
        )}

        {/* Error Fallback for Main Image */}
        {mainImageError ? (
          <div className="absolute inset-0 flex items-center justify-center bg-muted">
            <Icon name="ImageOff" size={48} className="text-muted-foreground" />
          </div>
        ) : (
          <Image
            src={store?.images?.[currentImageIndex]?.url}
            alt={store?.images?.[currentImageIndex]?.alt || `Immagine vetrina ${store?.name}`}
            className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${
              mainImageLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            loading="lazy"
            decoding="async"
            onLoad={() => setMainImageLoaded(true)}
            onError={() => setMainImageError(true)}
            style={{ width: '100%', height: '100%' }}
          />
        )}

        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

        {store?.images?.length > 1 && (
          <>
            <button
              onClick={(e) => handleImageNavigation('prev', e)}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-background/90 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-smooth focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-3"
              aria-label="Previous image"
            >
              <Icon name="ChevronLeft" size={16} />
            </button>
            <button
              onClick={(e) => handleImageNavigation('next', e)}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-background/90 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-smooth focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-3"
              aria-label="Next image"
            >
              <Icon name="ChevronRight" size={16} />
            </button>

            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
              {store?.images?.map((_, index) => (
                <div
                  key={index}
                  className={`h-1.5 rounded-full transition-all ${
                    index === currentImageIndex ? 'w-6 bg-white' : 'w-1.5 bg-white/50'
                  }`}
                />
              ))}
            </div>
          </>
        )}

        <button
          onClick={handleSaveToggle}
          className="absolute top-3 right-3 w-9 h-9 bg-background/90 backdrop-blur-sm rounded-full flex items-center justify-center transition-smooth hover:bg-background focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-3"
          aria-label={isSaved ? 'Rimuovi dai salvati' : 'Salva vetrina'}
        >
          <Icon
            name="Heart"
            size={18}
            className={isSaved ? 'text-error fill-error' : 'text-foreground'}
          />
        </button>

        {store?.badge && (
          <div className="absolute top-3 left-3 bg-primary text-primary-foreground px-2.5 py-1 rounded-lg font-caption font-semibold text-xs">
            {store?.badge}
          </div>
        )}

        <div className="absolute bottom-3 left-3 flex items-center gap-2 bg-background/90 backdrop-blur-sm rounded-lg px-2.5 py-1.5">
          <div className="relative w-8 h-8 rounded-full overflow-hidden flex-shrink-0 bg-muted">
            {/* Skeleton Loader for Logo */}
            {!logoLoaded && !logoError && (
              <div className="absolute inset-0 bg-gradient-to-r from-muted via-muted-foreground/10 to-muted animate-pulse" />
            )}

            {/* Error Fallback for Logo */}
            {logoError ? (
              <div className="absolute inset-0 flex items-center justify-center bg-muted">
                <Icon name="Store" size={16} className="text-muted-foreground" />
              </div>
            ) : (
              <Image
                src={store?.logo}
                alt={store?.logoAlt || `Logo ${store?.name}`}
                className={`w-full h-full object-cover ${
                  logoLoaded ? 'opacity-100' : 'opacity-0'
                }`}
                loading="lazy"
                decoding="async"
                onLoad={() => setLogoLoaded(true)}
                onError={() => setLogoError(true)}
                style={{ width: '32px', height: '32px' }}
              />
            )}
          </div>
        </div>
      </div>
      <div className="p-4 md:p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex-1 min-w-0">
            <h3 className="font-heading font-semibold text-base md:text-lg text-foreground mb-1 truncate">
              {store?.name}
            </h3>
            <p className="font-caption text-sm text-muted-foreground truncate">
              {store?.category}
            </p>
          </div>

          {store?.isOpen && (
            <div className="flex items-center gap-1.5 bg-success/10 text-success px-2.5 py-1 rounded-lg flex-shrink-0">
              <div className="w-2 h-2 bg-success rounded-full animate-pulse" />
              <span className="font-caption text-xs font-semibold whitespace-nowrap">Aperto</span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-4 mb-3">
          <div className="flex items-center gap-1">
            <Icon name="Star" size={16} className="text-warning fill-warning" />
            <span className="font-caption text-sm font-semibold text-foreground">
              {store?.rating}
            </span>
            <span className="font-caption text-xs text-muted-foreground">
              ({store?.reviewCount})
            </span>
          </div>

          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Icon name="MapPin" size={14} />
            <span className="font-caption text-xs truncate">{store?.zone}</span>
          </div>

          {store?.distance && (
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <Icon name="Navigation" size={14} />
              <span className="font-caption text-xs whitespace-nowrap">{store?.distance} km</span>
            </div>
          )}
        </div>

        {store?.promotion && (
          <div className="bg-accent/10 border border-accent/20 rounded-lg p-3 mb-3">
            <div className="flex items-start gap-2">
              <Icon name="Tag" size={16} className="text-accent flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="font-caption text-sm font-semibold text-accent mb-0.5">
                  Promozione attiva
                </p>
                <p className="font-caption text-xs text-foreground line-clamp-2">
                  {store?.promotion}
                </p>
              </div>
            </div>
          </div>
        )}

        {store?.cashback && (
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-3 mb-3">
            <div className="flex items-center gap-2">
              <Icon name="Coins" size={16} className="text-primary flex-shrink-0" />
              <p className="font-caption text-sm font-semibold text-primary">
                Cashback {store?.cashback}%
              </p>
            </div>
          </div>
        )}

        <div className="flex gap-2">
          <Button
            variant="default"
            size="sm"
            iconName="Eye"
            iconPosition="left"
            onClick={handleCardClick}
            fullWidth
          >
            Vedi Vetrina
          </Button>
        </div>
      </div>
    </div>
  );
};

export default StoreCard;